<template>
  <b-card no-body>
    <div class="m-2">
      <validation-observer ref="oldAccountOfficerRules">
        <b-form>
          <b-row class="ml-0 mr-2">
            <b-col
              md="6"
              class="mb-2"
            >
              <SelectField
                id="staff-name"
                v-model="batchAccountOfficer.staffData"
                :label="$t('Select an Account Officer')"
                :reduce="option => option"
                :options="accountOfficerOptions"
                :placeholder="$t('Select')"
                :loading="isAccountOfficerLoading"
                :error-name="$t('Account Officer')"
                item="label"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <b-row>
        <b-col
          md="6"
          class="mb-2"
        >
          <validation-observer ref="reassignAccountOfficerRules">
            <b-form>
              <b-col
                md="12"
              >
                <div>
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select File (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col
              md="5"
              class="mt-2"
            >
              <Button
                v-if="getUserPermission('BATCH_ACCOUNT_OFFICER_REASSIGNMENT')"
                :label="$t('Reassign Account Officer')"
                size="md"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleSubmit"
              />
            </b-col>
            <b-col
              md="7"
              class="mt-2"
            >
              <Button
                class="ml-2"
                align="end"
                :label="$t('Download Account Officer Reassignment Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BFormFile,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import Button from '../../components/button/Button.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../helpers/data'
import { accountPermissions } from '../../../helpers/permissions'
import SelectField from '../../forms/SelectField.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    SelectField,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
      ],
      required,
      isDisabled: true,
      isAccountOfficerLoading: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,

      sortDesc: false,
      sortBy: 'accountNumbers',
      accountOfficerOptions: [],
      batchAccountOfficer: {
        staffData: '',
      },
    }
  },
  mounted() {
    this.fetchAccountOfficers()
  },
  methods: {
    ...mapActions({
      getAccountOfficers: 'account/getAccountOfficerList',
      accountOfficerReassignment: 'batch/accountOfficerReassignmentAction',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async fetchAccountOfficers() {
      try {
        this.isAccountOfficerLoading = true
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.BATCH_ACCOUNT_OFFICER_REASSIGNMENT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.map(item => ({ label: `${item.name}`, value: item.staffID }))
          this.isAccountOfficerLoading = false
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isAccountOfficerLoading = false
      }
    },
    async handleSubmit() {
      this.$refs.reassignAccountOfficerRules.validate().then(success => {
        if (success) {
          this.confirm(this.createAccountOfficerReassignment, { text: this.$t('Are you sure you want to reassign to Account Officer?') })
        }
      })
    },
    async createAccountOfficerReassignment() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const accountNumbers = this.parseExcelFile.map(item => item.accountNumbers.toString())
        const { label, value } = this.batchAccountOfficer.staffData

        const formData = {
          newStaffID: value,
          newStaffName: label,
          accountNumbers,
        }
        const response = await this.accountOfficerReassignment({ formData, permission: accountPermissions.BATCH_ACCOUNT_OFFICER_REASSIGNMENT })
        const { status } = response
        if (status === 200) {
          this.alertSuccess({ text: this.$t('Your request to reassign batch account officer was successful') })
          this.hasFileData = []
          this.totalSheetRows = ''
          this.parseExcelFile = []
          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        const reasonText = this.$t('Your request to reassign batch account officer failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${reasonText} ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.isDisabled = false
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'dd/mm/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            accountNumbers: items['Account Numbers'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Account Officer Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const REASSIGN_ACCOUNT_OFFICER = 23
        const response = await this.downloadAccountFormat(REASSIGN_ACCOUNT_OFFICER)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Account-Officer-Reassignment.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Account Officer Reassignment Format was successful') })
        }
      } catch (error) {
        const text = this.$t('Your request to download Account Officer Reassignment Format failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${text} ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
